import type { VoiceCommandResult } from './voiceCommandClient';

const STORAGE_KEY = 'voicePendingContactConfirmation';
const DISAMBIGUATION_KEY = 'voicePendingContactDisambiguation';

export interface PendingContactConfirmation {
  intent: string;
  entityKey: string;
  extractedName: string;
  suggestedContact: {
    id: number;
    company_name?: string | null;
    contact_name?: string | null;
  };
  pendingEntities?: Record<string, unknown>;
}

export interface PendingContactDisambiguation {
  intent: string;
  entityKey: string;
  searchTerm: string;
  matchingContacts: Array<{
    id: number;
    company_name?: string | null;
    contact_name?: string | null;
  }>;
  pendingEntities?: Record<string, unknown>;
}

const AFFIRMATIVE = /^(yes|yeah|yep|yup|correct|right|sure|ok|okay|confirm|confirmed|go ahead|do it)$/i;
const NEGATIVE = /^(no|nope|nah|wrong|incorrect|not that|different)$/i;

export function isAffirmativeReply(text: string): boolean {
  return AFFIRMATIVE.test(text.trim());
}

export function isNegativeReply(text: string): boolean {
  return NEGATIVE.test(text.trim());
}

export function storePendingContactConfirmation(result: VoiceCommandResult): void {
  if (result.phase !== 'contact_confirmation' || !result.data?.suggested_contact) return;

  const pending: PendingContactConfirmation = {
    intent: result.data.intent || 'create_quote',
    entityKey: result.data.entity_key || 'customer_name',
    extractedName: result.data.extracted_name || '',
    suggestedContact: result.data.suggested_contact,
    pendingEntities: result.data.pending_entities,
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
}

export function storePendingContactDisambiguation(result: VoiceCommandResult): void {
  if (result.phase !== 'disambiguation' || !Array.isArray(result.data?.matching_customers)) return;

  const pending: PendingContactDisambiguation = {
    intent: result.data.intent || 'create_quote',
    entityKey: result.data.entity_key || 'customer_name',
    searchTerm: result.data.search_term || '',
    matchingContacts: result.data.matching_customers,
    pendingEntities: result.data.pending_entities,
  };

  localStorage.setItem(DISAMBIGUATION_KEY, JSON.stringify(pending));
}

export function getPendingContactConfirmation(): PendingContactConfirmation | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PendingContactConfirmation;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function getPendingContactDisambiguation(): PendingContactDisambiguation | null {
  const raw = localStorage.getItem(DISAMBIGUATION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PendingContactDisambiguation;
  } catch {
    localStorage.removeItem(DISAMBIGUATION_KEY);
    return null;
  }
}

export function clearPendingContactConfirmation(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function clearPendingContactDisambiguation(): void {
  localStorage.removeItem(DISAMBIGUATION_KEY);
}

export function clearAllPendingContactFlows(): void {
  clearPendingContactConfirmation();
  clearPendingContactDisambiguation();
}
